import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Group,
  Loader,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useQuery } from '@tanstack/react-query';
import { IconPrinter } from '@tabler/icons-react';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

// Phase Accounting-9: فواتير المبيعات — read-only register of the
// auto-issued statutory invoices, with the print view per row.

interface SalesInvoiceDto {
  salesInvoiceId: string;
  invoiceNumber: string;
  issuedAt: string;
  orderId: string;
  orderDisplayNumber: string;
  customerName: string;
  subtotalExTax: number;
  taxTotal: number;
  total: number;
}

interface SalesInvoicePageDto {
  total: number;
  rows: SalesInvoiceDto[];
}

export function SalesInvoicesPage() {
  const { t } = useTranslation();
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['sales-invoices', range, page] as const,
    queryFn: async () =>
      (
        await apiClient.get<SalesInvoicePageDto>('/api/accounting/sales-invoices', {
          params: {
            from: range[0] ? toIsoDate(range[0]) : undefined,
            to: range[1] ? toIsoDate(range[1]) : undefined,
            page,
            pageSize: 50,
          },
        })
      ).data,
  });

  const openPrint = async (id: string) => {
    // Bearer auth can't ride window.open — fetch the HTML, then open a
    // blob URL. The print button inside the page does the rest.
    const resp = await apiClient.get<Blob>(`/api/accounting/sales-invoices/${id}/print`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(
      new Blob([resp.data], { type: 'text/html' }),
    );
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.salesInvoices.title')}
        subtitle={t('accounting.salesInvoices.subtitle')}
      />

      <Paper p="md">
        <DatePickerInput
          type="range"
          value={range}
          onChange={setRange}
          placeholder={t('accounting.journal.rangePlaceholder')}
          clearable
          style={{ maxWidth: 300 }}
        />
      </Paper>

      {isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.salesInvoices.columns.number')}</Table.Th>
                <Table.Th>{t('accounting.salesInvoices.columns.date')}</Table.Th>
                <Table.Th>{t('accounting.salesInvoices.columns.customer')}</Table.Th>
                <Table.Th>{t('accounting.salesInvoices.columns.order')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.salesInvoices.columns.vat')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.salesInvoices.columns.total')}</Table.Th>
                <Table.Th style={{ width: 60 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data?.rows ?? []).map((inv) => (
                <Table.Tr key={inv.salesInvoiceId}>
                  <Table.Td className="font-numeric">{inv.invoiceNumber}</Table.Td>
                  <Table.Td className="font-numeric">{inv.issuedAt.slice(0, 10)}</Table.Td>
                  <Table.Td>{inv.customerName}</Table.Td>
                  <Table.Td className="font-numeric">{inv.orderDisplayNumber}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {inv.taxTotal.toFixed(2)}
                  </Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {inv.total.toFixed(2)}
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label={t('accounting.salesInvoices.print')}>
                      <ActionIcon variant="subtle" onClick={() => openPrint(inv.salesInvoiceId)}>
                        <IconPrinter size={16} stroke={1.6} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
              {data && data.rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text size="sm" c="dimmed" ta="center" py="lg">
                      {t('accounting.salesInvoices.empty')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          {data && data.total > 50 && (
            <Group justify="center" p="md">
              <Pagination total={Math.ceil(data.total / 50)} value={page} onChange={setPage} />
            </Group>
          )}
        </Paper>
      )}
    </Stack>
  );
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
